import { useId, useState, type ReactNode } from "react";

import { cn } from "../lib/cn";

/**
 * Charts for the dashboards and reports (P5-09). Single-series by design (one measure per chart, never two y-axes):
 * the title names the series, so there is no legend. Marks use the ink token (dark bars on light, light on dark) and
 * the brand yellow on hover/focus; text stays in text tokens. Every chart has a hover/focus tooltip and a visually
 * hidden table for screen readers.
 */

export interface TrendPoint {
  /** x label, e.g. "9 Oct" */
  label: string;
  value: number;
  /** formatted value for the tooltip and table, e.g. "₹12,400" */
  display: string;
}

export interface TrendChartProps {
  data: TrendPoint[];
  /** accessible name, e.g. "Sales per day" */
  title: string;
  /** column headers of the hidden table */
  labels?: { x: string; y: string };
  emptyLabel?: ReactNode;
  className?: string;
}

const W = 560;
const H = 240;
const PAD = { top: 22, right: 8, bottom: 32, left: 8 };
const GAP = 2;

/** Bars over time (orders, sales per day/week). Heights from a zero baseline; 4px rounded tops. */
export function TrendChart({
  data,
  title,
  labels = { x: "Date", y: "Value" },
  emptyLabel = "No data for this period",
  className,
}: TrendChartProps) {
  const [active, setActive] = useState<number | null>(null);
  const id = useId();
  const max = Math.max(0, ...data.map((d) => d.value));
  if (data.length === 0 || max === 0) {
    return (
      <div
        className={cn(
          "flex h-56 items-center justify-center rounded-lg text-sm text-muted-foreground",
          className,
        )}
      >
        {emptyLabel}
      </div>
    );
  }
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const slot = plotW / data.length;
  const bar = Math.max(2, Math.min(40, slot - GAP));
  const every = Math.ceil(data.length / 6); // at most ~6 x labels
  const point = active === null ? null : data[active];
  return (
    <figure className={cn("relative", className)} aria-labelledby={`${id}-title`}>
      <figcaption id={`${id}-title`} className="sr-only">
        {title}
      </figcaption>
      <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" role="img" aria-label={title}>
        {[0.25, 0.5, 0.75, 1].map((f) => (
          <line
            key={f}
            x1={PAD.left}
            x2={W - PAD.right}
            y1={PAD.top + plotH * (1 - f)}
            y2={PAD.top + plotH * (1 - f)}
            className="stroke-border"
            strokeWidth={1}
          />
        ))}
        {data.map((d, i) => {
          const h = (d.value / max) * plotH;
          const x = PAD.left + i * slot + (slot - bar) / 2;
          const y = PAD.top + plotH - h;
          const r = Math.min(4, bar / 2, h);
          return (
            <g key={`${d.label}-${i}`}>
              <path
                d={`M${x},${PAD.top + plotH} V${y + r} Q${x},${y} ${x + r},${y} H${x + bar - r} Q${x + bar},${y} ${x + bar},${y + r} V${PAD.top + plotH} Z`}
                className={active === i ? "fill-brand" : "fill-foreground"}
              />
              {/* hit target: the whole column, bigger than the mark */}
              <rect
                x={PAD.left + i * slot}
                y={PAD.top}
                width={slot}
                height={plotH}
                fill="transparent"
                tabIndex={0}
                aria-label={`${d.label}: ${d.display}`}
                onMouseEnter={() => setActive(i)}
                onMouseLeave={() => setActive(null)}
                onFocus={() => setActive(i)}
                onBlur={() => setActive(null)}
              />
              {i % every === 0 && (
                <text
                  x={i === 0 ? PAD.left : PAD.left + i * slot + slot / 2}
                  y={H - 8}
                  textAnchor={i === 0 ? "start" : "middle"}
                  className="fill-muted-foreground text-[15px]"
                >
                  {d.label}
                </text>
              )}
            </g>
          );
        })}
        {/* scale: the top gridline is the period's highest value */}
        <text x={PAD.left} y={PAD.top - 6} className="fill-muted-foreground text-[13px]">
          {data.find((d) => d.value === max)?.display}
        </text>
        <line
          x1={PAD.left}
          x2={W - PAD.right}
          y1={PAD.top + plotH}
          y2={PAD.top + plotH}
          className="stroke-muted-foreground"
          strokeWidth={1}
        />
      </svg>
      {point && (
        <div
          role="status"
          className="pointer-events-none absolute top-0 right-0 rounded-md border border-border bg-surface px-3 py-1.5 text-xs shadow-card"
        >
          <span className="text-muted-foreground">{point.label}</span>{" "}
          <span className="font-semibold text-foreground">{point.display}</span>
        </div>
      )}
      <table className="sr-only">
        <caption>{title}</caption>
        <thead>
          <tr>
            <th scope="col">{labels.x}</th>
            <th scope="col">{labels.y}</th>
          </tr>
        </thead>
        <tbody>
          {data.map((d, i) => (
            <tr key={`${d.label}-${i}`}>
              <td>{d.label}</td>
              <td>{d.display}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}

export interface RankItem {
  label: ReactNode;
  value: number;
  display: string;
  hint?: ReactNode;
}

/** Top-N list with proportional bars (best sellers, top stores, payment methods). */
export function RankList({
  items,
  title,
  emptyLabel = "Nothing yet",
  className,
}: {
  items: RankItem[];
  title: string;
  emptyLabel?: ReactNode;
  className?: string;
}) {
  const max = Math.max(0, ...items.map((i) => i.value));
  if (items.length === 0 || max === 0) {
    return <p className={cn("text-sm text-muted-foreground", className)}>{emptyLabel}</p>;
  }
  return (
    <ol className={cn("space-y-3", className)} aria-label={title}>
      {items.map((item, index) => (
        <li key={index} className="space-y-1 text-sm">
          <div className="flex items-baseline justify-between gap-3">
            <span className="min-w-0 truncate">
              {item.label}
              {item.hint && <span className="ml-2 text-xs text-muted-foreground">{item.hint}</span>}
            </span>
            <span className="shrink-0 font-medium">{item.display}</span>
          </div>
          <div className="h-2 rounded-full bg-surface-muted" aria-hidden>
            <div
              className="h-2 rounded-full bg-foreground"
              style={{ width: `${Math.max(2, (item.value / max) * 100)}%` }}
            />
          </div>
        </li>
      ))}
    </ol>
  );
}
